import { stringify } from 'qs';

import { BulkExpenseActionResponse } from '@Shared/api_responses';
import { IExpenseData } from '@Shared/expense_data';
import { IExpenseCombinedModel } from '@Shared/helpers/dataMappers/expenseMappers';
import { getTransactionFiltersFromSearchApiFilter } from '@Shared/helpers/dataMappers/filterMappers';
import { ISearchTransactionsParams } from '@Shared/interfaces/Filters';
import { ICardTransactionModel, IExpenseActivityModel, IExpenseModel, IExpenseTagBucketModel } from '@Shared/models';

import { Api as apiService } from '../api';

export interface ISearchTransactionsResponse {
  amountSum: number;
  count: number;
  rows: IExpenseModel[];
  with_inbox_count: number;
}

export interface ISearchAccountingSyncExpensesResponse {
  amountSum: number;
  count: number;
  rows: IExpenseModel[];
}

export interface IUnapproveExpenseData {
  note?: string;
}

export interface IUnapproveExpenseResponse {
  created_at: number;
  details: {
    previousState: string;
  };
  details_string_unicode: string;
  expense_id: string;
  id: string;
  type: 'reversal';
  user_id: string;
}

export interface ISubmitTransactionResponse {
  transactions: IExpenseModel[];
  card_transaction: ICardTransactionModel;
}

export interface ISubmissionFailedExpenseData {
  card_transaction_id: string;
  reason: string;
}

export interface IBulkSubmitTransactionsResponse extends BulkExpenseActionResponse {
  /**
   * @deprecated in favor of BulkExpenseActionResponse.success
   */
  successes: string[];
  /**
   * @deprecated in favor of BulkExpenseActionResponse.failed
   */
  failures: ISubmissionFailedExpenseData[];
}

export abstract class TransactionsApi {
  static readonly transactionsApiURL: string = '/api/web/transactions';

  static includeWithExpenses = [
    'include[user]',
    'include[user_profile]',
    'include[venue_info]',
    'include[image]',
    'include[images]',
    'include[category]',
    'include[expense_tags]',
    'include[metadata]',
    'include[manager]',
    'include[card_transaction]',
    'include[card]',
    'include[trip]'
  ].join('&');

  static searchTransactions(params: ISearchTransactionsParams): Promise<ISearchTransactionsResponse> {
    const query = stringify(params);
    return apiService.responseToJSONAndToastError(apiService.get(TransactionsApi.transactionsApiURL + `/search?${query}`));
  }

  static searchTransactionsTotals(params: ISearchTransactionsParams): Promise<ISearchTransactionsResponse> {
    const query = stringify(params);
    return apiService.responseToJSONAndToastError(apiService.get(TransactionsApi.transactionsApiURL + `/search/totals?${query}`));
  }

  static searchCombinedTransactions(section: string, params: ISearchTransactionsParams): Promise<ISearchTransactionsResponse> {
    const query = stringify(params);
    return apiService.responseToJSONAndToastError(apiService.get(TransactionsApi.transactionsApiURL + `/combined/${section}?${query}`));
  }

  static getTransactionsTotals(section: string, params: ISearchTransactionsParams): Promise<ISearchTransactionsResponse> {
    const query = stringify(params);
    return apiService.responseToJSONAndToastError(
      apiService.get(TransactionsApi.transactionsApiURL + `/combined/${section}/totals?${query}`)
    );
  }

  static approve(expenseId: string): Promise<IExpenseModel> {
    return apiService.responseToJSONAndToastError(
      apiService.post(TransactionsApi.transactionsApiURL + `/${expenseId}/approve?${TransactionsApi.includeWithExpenses}`)
    );
  }

  static submit(payload: IExpenseData): Promise<ISubmitTransactionResponse> {
    return apiService.responseToJSONAndToastError(apiService.post('/api/web/card_transaction', payload));
  }

  static submitWithoutToastWrapper(payload: IExpenseData): Promise<Response> {
    return apiService.post('/api/web/card_transaction', payload);
  }

  static bulkSubmit(expenseDataList: IExpenseData[]): Promise<IBulkSubmitTransactionsResponse> {
    return apiService.responseToJSONAndToastError(
      apiService.post('/api/web/card_transaction/bulk_submit', { expense_data_list: expenseDataList })
    );
  }

  static queue(payload: IExpenseData): Promise<Response> {
    return apiService.responseWithToastError(apiService.post('/api/unsubmitted_expenses/queued', payload));
  }

  static queueWithoutToastWrapper(payload: IExpenseData): Promise<Response> {
    return apiService.post('/api/unsubmitted_expenses/queued', payload);
  }

  static unapprove(expenseId: string, data: IUnapproveExpenseData): Promise<IUnapproveExpenseResponse> {
    return apiService.responseToJSONAndToastError(apiService.put(TransactionsApi.transactionsApiURL + `/${expenseId}/reverse`, data));
  }

  static deny(expenseId: string, data: { resubmittable: boolean; note?: string }): Promise<IExpenseModel> {
    return apiService.responseToJSONAndToastError(
      apiService.post(TransactionsApi.transactionsApiURL + `/${expenseId}/deny?${TransactionsApi.includeWithExpenses}`, data)
    );
  }

  static deleteOne(expenseId: string): Promise<IExpenseModel> {
    return apiService.responseToJSONAndToastError(apiService.delete(TransactionsApi.transactionsApiURL + `/${expenseId}`));
  }

  static editAmount(expenseId: string, data: { amount: number; message: string; internationalAmount: string }): Promise<any> {
    return apiService.responseToJSONAndToastError(apiService.put(TransactionsApi.transactionsApiURL + `/${expenseId}/amount`, data));
  }

  static addComment(expenseId: string, message: string): Promise<IExpenseActivityModel> {
    return apiService.responseToJSONAndToastError(apiService.post(`/api/web/transactions/${expenseId}/comment`, { message }));
  }

  static approveAll(body: any): Promise<IExpenseModel[]> {
    // The users of this method (ReimbursableExpenseBulkActions/TransactionsBulkActions) have their own error parsing/toasting behaviour
    // We shouldn't use responseToJSONAndToastError here
    return apiService.responseToJSON(apiService.post(TransactionsApi.transactionsApiURL + `/approveAll`, body));
  }

  static denyAll(body: any): Promise<IExpenseModel[]> {
    return apiService.responseToJSONAndToastError(apiService.post(TransactionsApi.transactionsApiURL + `/denyAll`, body));
  }

  static reassignAll(body: any): Promise<{
    failures: any[];
    successes: any[];
  }> {
    return apiService.responseToJSONAndToastError(apiService.put(TransactionsApi.transactionsApiURL + `/manager/bulk/reassign`, body));
  }

  static getActivities(expenseId: string): Promise<any> {
    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/transactions/${expenseId}/activity?include[user]`));
  }

  static getSplits(splitTransactionId: string): Promise<{ expenses: IExpenseModel[] }> {
    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/split_transactions/${splitTransactionId}`));
  }

  static getCombinedTransactions(
    params: ISearchTransactionsParams,
    tab: 'pending' | 'pending_submission' | 'search',
    activeExpenseBucketsById?: Record<string, IExpenseTagBucketModel>,
    forPersonalCard?: boolean
  ): Promise<ISearchTransactionsResponse> {
    const searchParams = new URLSearchParams({
      limit: params.limit?.toString(),
      offset: params.offset?.toString(),
      include: JSON.stringify(['ocrReceipts', 'card'])
    });

    if (params.query) {
      searchParams.append('search', params.query.toString());
    }

    if (forPersonalCard) {
      searchParams.append('forPersonalCard', 'true');
    }

    if (params.sort) {
      searchParams.append('sortField', params.sort);
      searchParams.append('sortDirection', params.direction);
    }

    const filters = getTransactionFiltersFromSearchApiFilter(params.filters, activeExpenseBucketsById);
    if (filters) {
      searchParams.append('filters', JSON.stringify(filters));
    }

    return apiService.responseToJSONAndToastError(apiService.get(`/api/web/transactions/combined/${tab}?${searchParams}`));
  }

  static getUnexpensedTransactions(
    params: ISearchTransactionsParams,
    activeExpenseBucketsById?: Record<string, IExpenseTagBucketModel>
  ): Promise<ISearchTransactionsResponse> {
    return TransactionsApi.getCombinedTransactions(params, 'pending', activeExpenseBucketsById);
  }

  static skip(cardTransaction: IExpenseCombinedModel): Promise<IExpenseCombinedModel> {
    return apiService.responseToJSONAndToastError(
      apiService.post(`/api/web/accounts/cards/transactions/${cardTransaction.id}`, { status: 'skipped' })
    );
  }

  static unskip(cardTransaction: IExpenseCombinedModel): Promise<IExpenseCombinedModel> {
    return apiService.responseToJSONAndToastError(
      apiService.post(`/api/web/accounts/cards/transactions/${cardTransaction.id}`, { status: 'new' })
    );
  }

  static update(expense: IExpenseCombinedModel): Promise<IExpenseCombinedModel> {
    return apiService.responseToJSONAndToastError(apiService.put(`/api/web/transactions/${expense.id}`, expense));
  }

  static getAccountingSyncTransactions(params: ISearchTransactionsParams): Promise<ISearchAccountingSyncExpensesResponse> {
    const query = stringify(params);
    return apiService.responseToJSONAndToastError(
      apiService.get(TransactionsApi.transactionsApiURL + `/accounting_sync_expenses?${query}`)
    );
  }
  static bulkEditExpenses(expense_ids: string[], changes: Record<string, any>) {
    return apiService.put(`/api/web/transactions/bulkedit`, { expense_ids, changes });
  }
}
